import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Divider,
  Chip,
  IconButton,
  Alert,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Warehouse as WarehouseIcon,
  Business as BranchIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  StockCount,
  CreateStockCountDto,
  UpdateStockCountDto,
  UpdateStockCountDetail,
} from '../../types/stockCounts.types';
import { stockCountsService } from '../../services/stockCounts.service';
import { warehousesService } from '../../services/warehouses.service';
import { branchesService } from '../../services/branches.service';
import { Warehouse } from '../../types/warehouses.types';
import { Branch } from '../../types/branches.types';
import { ItemUnit } from '../../types/items.types';
import axios from 'axios';
import config from '../../config';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';
import ManualCostEditDialog, { ManualCostLineRow } from '../dialogs/ManualCostEditDialog';

const translations = {
  en: {
    addStockCount: 'New Stock Count',
    editStockCount: 'Update Physical Quantities',
    basicInfo: 'Basic Information',
    selectLocation: 'Select Location',
    countNumber: 'Count Number',
    countDate: 'Count Date',
    countType: 'Count Type',
    warehouse: 'Warehouse',
    branch: 'Branch',
    details: 'Count Details',
    item: 'Item',
    systemQty: 'System Qty',
    physicalQty: 'Physical Qty',
    variance: 'Variance',
    varianceValue: 'Value',
    status: 'Status',
    save: 'Save',
    cancel: 'Cancel',
    notes: 'Notes',
    full: 'Full Count',
    partial: 'Partial Count',
    cycle: 'Cycle Count',
    itemsLoaded: 'Items will be loaded automatically after selection',
    selectWarehouseOrBranch: 'Please select either Warehouse or Branch',
    locationSelected: 'items ready for counting',
    stockCountCreated: 'Stock count created successfully',
    stockCountUpdated: 'Stock count updated successfully',
    failedToSave: 'Failed to save stock count',
    duplicateStockCount: 'A stock count already exists for this branch during this period',
    loadDataError: 'Failed to load data',
    cannotSelectBoth: 'Cannot select both warehouse and branch',
    unit: 'Unit',
    displayUnit: 'Count Unit',
  },
  ar: {
    addStockCount: 'جرد جديد',
    editStockCount: 'تحديث الكميات الفعلية',
    basicInfo: 'المعلومات الأساسية',
    selectLocation: 'اختر الموقع',
    countNumber: 'رقم الجرد',
    countDate: 'تاريخ الجرد',
    countType: 'نوع الجرد',
    warehouse: 'المخزن',
    branch: 'الفرع',
    details: 'تفاصيل الجرد',
    item: 'الصنف',
    systemQty: 'كمية النظام',
    physicalQty: 'الكمية الفعلية',
    variance: 'الفرق',
    varianceValue: 'القيمة',
    status: 'الحالة',
    save: 'حفظ',
    cancel: 'إلغاء',
    notes: 'ملاحظات',
    full: 'جرد كامل',
    partial: 'جرد جزئي',
    cycle: 'جرد دوري',
    itemsLoaded: 'سيتم تحميل الأصناف تلقائياً بعد الاختيار',
    selectWarehouseOrBranch: 'يرجى اختيار المخزن أو الفرع',
    locationSelected: 'صنف جاهز للجرد',
    stockCountCreated: 'تم إنشاء الجرد بنجاح',
    stockCountUpdated: 'تم تحديث الجرد بنجاح',
    failedToSave: 'فشل حفظ الجرد',
    duplicateStockCount: 'يوجد جرد سابق لنفس الفرع خلال هذه الفترة',
    loadDataError: 'فشل تحميل البيانات',
    cannotSelectBoth: 'لا يمكن اختيار المخزن والفرع معاً',
    unit: 'الوحدة',
    displayUnit: 'وحدة الجرد',
  },
};

interface StockCountDialogProps {
  open: boolean;
  onClose: () => void;
  stockCount?: StockCount;
  onSave: () => void;
}

const StockCountDialog: React.FC<StockCountDialogProps> = ({
  open,
  onClose,
  stockCount,
  onSave,
}) => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { minDate, maxDate } = useFiscalDateRange();
  const isSuperAdmin = useSelector((state: RootState) => state.auth.user?.isSuperAdmin) ?? false;
  const userPermissions = useSelector((state: RootState) => state.auth.permissions) ?? [];
  const canOverrideCost = isSuperAdmin || userPermissions.includes('STOCK_COST_OVERRIDE');
  const isEditMode = !!stockCount;
  const dateLocked = isEditMode && !isSuperAdmin;
  const [manualCostDialogOpen, setManualCostDialogOpen] = useState(false);

  const gridRef = useRef<HTMLDivElement>(null);
  useGridEnterNavigation(gridRef);

  const [formData, setFormData] = useState({
    countNumber: '',
    countDate: new Date().toISOString().split('T')[0],
    countType: 'Full',
    warehouseId: null as number | null,
    branchId: null as number | null,
    notes: '',
  });

  const [locationType, setLocationType] = useState<'warehouse' | 'branch' | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [detailLines, setDetailLines] = useState<UpdateStockCountDetail[]>([]);
  // Raw input strings for physicalQuantity inputs — preserves trailing zeros/dots while typing
  // (parseFloat strips them, so we display this string instead of the parsed number when available)
  const [physicalQuantityInputs, setPhysicalQuantityInputs] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isWarehouseKeeper, setIsWarehouseKeeper] = useState(false);

  // Unit selection per detail line: key = countDetailId, value = selected ItemUnit
  const [itemUnitsMap, setItemUnitsMap] = useState<Record<number, ItemUnit[]>>({});
  const [selectedDisplayUnits, setSelectedDisplayUnits] = useState<Record<number, ItemUnit | null>>({});

  const countTypes = [
    { value: 'Full', label: t.full },
    { value: 'Partial', label: t.partial },
    { value: 'Cycle', label: t.cycle },
  ];

  useEffect(() => {
    if (open) {
      loadMasterData();
    }
  }, [open]);

  useEffect(() => {
    if (open && warehouses.length > 0 && (branches.length > 0 || isWarehouseKeeper)) {
      if (stockCount) {
        loadStockCount();
      } else {
        loadNewCountNumber();
      }
    }
  }, [open, stockCount, warehouses, branches, isWarehouseKeeper]);

  // Load available units for each item when stockCount loads
  useEffect(() => {
    if (stockCount && stockCount.details.length > 0) {
      loadItemUnits();
    }
  }, [stockCount]);

  const loadItemUnits = async () => {
    if (!stockCount) return;
    const token = localStorage.getItem('token');
    const uniqueItemIds = Array.from(new Set(stockCount.details.map(d => d.itemId)));
    const unitsMap: Record<number, ItemUnit[]> = {};
    const defaultUnits: Record<number, ItemUnit | null> = {};

    await Promise.all(uniqueItemIds.map(async (itemId) => {
      try {
        const res = await axios.get(`${config.API_BASE_URL}/Items/${itemId}/units`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        unitsMap[itemId] = res.data;
      } catch { unitsMap[itemId] = []; }
    }));

    // Set default display unit for each detail line (match the detail's unitId)
    stockCount.details.forEach(detail => {
      const units = unitsMap[detail.itemId] || [];
      const matchingUnit = units.find(u => u.unitId === detail.unitId);
      defaultUnits[detail.countDetailId] = matchingUnit || null;
    });

    setItemUnitsMap(unitsMap);
    setSelectedDisplayUnits(defaultUnits);
  };

  const loadMasterData = async () => {
    try {
      const [warehousesData, branchesData] = await Promise.all([
        warehousesService.getAll(),
        branchesService.getAll(),
      ]);

      // Read user branches from JWT token
      const token = localStorage.getItem('token');
      let userBranchIds: number[] = [];
      let userWarehouseIds: number[] = [];

      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
          const allowedBranches = payload.AllowedBranchId
            ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
            : [];
          userBranchIds = Array.from(new Set([...primaryBranch, ...allowedBranches]));

          const keeperClaims = payload.KeeperWarehouseId;
          userWarehouseIds = Array.isArray(keeperClaims) ? keeperClaims.map(Number) : keeperClaims ? [Number(keeperClaims)] : [];
        } catch (e) { console.error('Failed to parse token:', e); }
      }

      setIsWarehouseKeeper(userWarehouseIds.length > 0);

      // Filter warehouses: by keeper (warehouse keeper) or by branch (branch manager) or all
      const filteredWarehouses = userWarehouseIds.length > 0
        ? warehousesData.filter((w: Warehouse) => userWarehouseIds.includes(w.warehouseId))
        : userBranchIds.length === 0
          ? warehousesData
          : warehousesData.filter((w: Warehouse) =>
              w.branchId && userBranchIds.includes(w.branchId)
            );
      setWarehouses(filteredWarehouses);

      // Filter branches: warehouse keeper sees no branches, branch manager sees his branches, admin sees all
      const filteredBranches = userWarehouseIds.length > 0
        ? [] // Warehouse keeper doesn't need branches
        : userBranchIds.length === 0
          ? branchesData
          : branchesData.filter((b: Branch) => userBranchIds.includes(b.branchId));
      setBranches(filteredBranches);

      // Auto-select warehouse mode for warehouse keeper
      if (userWarehouseIds.length > 0) {
        setLocationType('warehouse');
      }
    } catch (error) {
      toast.error(t.loadDataError, { position: 'top-center', autoClose: 3000 });
    }
  };

  const loadStockCount = () => {
    if (!stockCount) return;

    setFormData({
      countNumber: stockCount.countNumber,
      countDate: stockCount.countDate.split('T')[0],
      countType: stockCount.countType || 'Full',
      warehouseId: stockCount.warehouseId || null,
      branchId: stockCount.branchId || null,
      notes: stockCount.notes || '',
    });

    if (stockCount.warehouseId) {
      setLocationType('warehouse');
      const warehouse = warehouses.find(w => w.warehouseId === stockCount.warehouseId);
      setSelectedWarehouse(warehouse || null);
    } else if (stockCount.branchId) {
      setLocationType('branch');
      const branch = branches.find(b => b.branchId === stockCount.branchId);
      setSelectedBranch(branch || null);
    }

    const lines = stockCount.details.map((detail) => ({
      countDetailId: detail.countDetailId,
      physicalQuantity: detail.physicalQuantity,
      notes: detail.notes || '',
    }));
    setDetailLines(lines);
    setPhysicalQuantityInputs({});
  };

  const loadNewCountNumber = async () => {
    try {
      const countNumber = await stockCountsService.generateCountNumber();
      setFormData((prev) => ({ ...prev, countNumber }));
    } catch (error) {
      toast.error(t.loadDataError, { position: 'top-center', autoClose: 3000 });
    }
  };

  const handleLocationTypeChange = (_event: React.MouseEvent<HTMLElement>, newType: 'warehouse' | 'branch' | null) => {
    if (newType !== null) {
      setLocationType(newType);
      if (newType === 'warehouse') {
        setSelectedBranch(null);
        setFormData({ ...formData, branchId: null });
      } else {
        setSelectedWarehouse(null);
        setFormData({ ...formData, warehouseId: null });
      }
      setErrors({});
    }
  };

  const updateLine = (countDetailId: number, field: keyof UpdateStockCountDetail, value: any) => {
    setDetailLines((prev) =>
      prev.map((line) => (line.countDetailId === countDetailId ? { ...line, [field]: value } : line))
    );
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.warehouseId && !formData.branchId) {
      newErrors.location = t.selectWarehouseOrBranch;
    }
    if (formData.warehouseId && formData.branchId) {
      newErrors.location = t.cannotSelectBoth;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Convert system quantity from original unit to display unit
  const getDisplaySystemQty = (detail: any): number => {
    const selectedUnit = selectedDisplayUnits[detail.countDetailId];
    if (!selectedUnit) return detail.systemQuantity;
    const originalUnit = (itemUnitsMap[detail.itemId] || []).find((u: ItemUnit) => u.unitId === detail.unitId);
    if (!originalUnit) return detail.systemQuantity;
    // originalCF = how many base units in original unit, displayCF = how many base in display unit
    return detail.systemQuantity * (originalUnit.conversionFactor / selectedUnit.conversionFactor);
  };

  // Convert physical quantity entered in display unit BACK to original unit for saving
  const convertToOriginalUnit = (detail: any, displayQty: number): number => {
    const selectedUnit = selectedDisplayUnits[detail.countDetailId];
    if (!selectedUnit) return displayQty;
    const originalUnit = (itemUnitsMap[detail.itemId] || []).find((u: ItemUnit) => u.unitId === detail.unitId);
    if (!originalUnit) return displayQty;
    return displayQty * (selectedUnit.conversionFactor / originalUnit.conversionFactor);
  };

  const handleUnitChange = (countDetailId: number, newUnit: ItemUnit | null) => {
    setSelectedDisplayUnits(prev => ({ ...prev, [countDetailId]: newUnit }));
    // Reset physical quantity when unit changes to avoid confusion
    updateLine(countDetailId, 'physicalQuantity', undefined);
    setPhysicalQuantityInputs(prev => {
      const { [countDetailId]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      if (stockCount) {
        const updateDto: UpdateStockCountDto = {
          stockCountId: stockCount.stockCountId,
          countDate: formData.countDate,
          countType: formData.countType,
          notes: formData.notes || undefined,
          details: detailLines.map(line => {
            const detail = stockCount.details.find(d => d.countDetailId === line.countDetailId);
            let qty = line.physicalQuantity;
            // Convert from display unit back to original unit
            if (qty !== undefined && qty !== null && detail) {
              qty = convertToOriginalUnit(detail, qty);
            }
            return {
              countDetailId: line.countDetailId,
              physicalQuantity: qty,
              notes: line.notes || undefined,
            };
          }),
        };
        await stockCountsService.update(stockCount.stockCountId, updateDto);
        toast.success(t.stockCountUpdated, { 
          position: 'top-center', 
          autoClose: 2000,
          style: { fontSize: '16px', fontWeight: 'bold' }
        });
      } else {
        const createDto: CreateStockCountDto = {
          countNumber: formData.countNumber,
          countDate: formData.countDate,
          countType: formData.countType,
          warehouseId: formData.warehouseId || undefined,
          branchId: formData.branchId || undefined,
          notes: formData.notes || undefined,
        };
        await stockCountsService.create(createDto);
        toast.success(t.stockCountCreated, { 
          position: 'top-center', 
          autoClose: 2000,
          style: { fontSize: '16px', fontWeight: 'bold' }
        });
      }
      onSave();
      handleClose();
    } catch (error: any) {
      let errorMessage = t.failedToSave;
      
      if (error.response?.data) {
        const data = error.response.data;
        
        if (data.message && data.message.toLowerCase().includes('already exists')) {
          errorMessage = t.duplicateStockCount;
        } 
        else if (data.message) {
          errorMessage = data.message;
        }
        else if (typeof data === 'string') {
          errorMessage = data;
        }
      }
      
      toast.error(errorMessage, { 
        position: 'top-center', 
        autoClose: 5000,
        style: { fontSize: '16px', fontWeight: 'bold' }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      countNumber: '',
      countDate: new Date().toISOString().split('T')[0],
      countType: 'Full',
      warehouseId: null,
      branchId: null,
      notes: '',
    });
    setDetailLines([]);
    setSelectedWarehouse(null);
    setSelectedBranch(null);
    setLocationType(null);
    setErrors({});
    setItemUnitsMap({});
    setSelectedDisplayUnits({});
    onClose();
  };

  const getVarianceColor = (variance?: number) => {
    if (!variance || variance === 0) return 'default';
    return variance > 0 ? 'success' : 'error';
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="xl" 
      fullWidth 
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '95vh',
          borderRadius: 3,
          bgcolor: isDarkMode ? '#1a1a2e' : 'white',
        }
      }}
    >
      <DialogTitle sx={{ 
        background: isDarkMode ? 'linear-gradient(135deg, #0f3460 0%, #16213e 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        color: 'white', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        py: 2,
        fontWeight: 'bold'
      }}>
        {stockCount ? t.editStockCount : t.addStockCount}
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#667eea' }}>
            {t.basicInfo}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label={t.countNumber}
                value={formData.countNumber}
                disabled
                sx={{ flex: 1 }}
              />

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t.countDate}
                  value={formData.countDate ? new Date(formData.countDate) : null}
                  onChange={(date) => setFormData({ ...formData, countDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                  format="dd/MM/yyyy"
                  views={['year', 'month', 'day']}
                  openTo="day"
                  disabled={dateLocked}
                  minDate={minDate}
                  maxDate={maxDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true, sx: { flex: 1 } } }}
                />
              </LocalizationProvider>

              <Autocomplete
                options={countTypes}
                getOptionLabel={(option) => option.label}
                value={countTypes.find(ct => ct.value === formData.countType) || null}
                onChange={(_, newValue) => {
                  setFormData({ ...formData, countType: newValue?.value || 'Full' });
                }}
                disabled={!!stockCount}
                renderInput={(params) => (
                  <TextField {...params} label={t.countType} />
                )}
                sx={{ flex: 1 }}
              />
            </Box>

            {!stockCount && (
              <>
                <Divider />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#667eea' }}>
                  {t.selectLocation}
                </Typography>

                {isWarehouseKeeper ? (
                  /* Warehouse keeper: only warehouse selection, no toggle */
                  <Autocomplete
                    options={warehouses}
                    getOptionLabel={(option) => `${option.warehouseCode} - ${isRTL ? option.warehouseNameAr : option.warehouseName}`}
                    value={selectedWarehouse}
                    onChange={(_, newValue) => {
                      setSelectedWarehouse(newValue);
                      setFormData({ ...formData, warehouseId: newValue?.warehouseId || null });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t.warehouse}
                        error={!!errors.location}
                        helperText={errors.location}
                      />
                    )}
                  />
                ) : (
                  /* Admin / Branch Manager: toggle between warehouse and branch */
                  <>
                    <ToggleButtonGroup
                      value={locationType}
                      exclusive
                      onChange={handleLocationTypeChange}
                      fullWidth
                      sx={{ mb: 1 }}
                    >
                      <ToggleButton value="warehouse" sx={{ py: 1.5 }}>
                        <WarehouseIcon sx={{ mr: 1 }} />
                        {t.warehouse}
                      </ToggleButton>
                      <ToggleButton value="branch" sx={{ py: 1.5 }}>
                        <BranchIcon sx={{ mr: 1 }} />
                        {t.branch}
                      </ToggleButton>
                    </ToggleButtonGroup>

                    {locationType === 'warehouse' && (
                      <Autocomplete
                        options={warehouses}
                        getOptionLabel={(option) => `${option.warehouseCode} - ${isRTL ? option.warehouseNameAr : option.warehouseName}`}
                        value={selectedWarehouse}
                        onChange={(_, newValue) => {
                          setSelectedWarehouse(newValue);
                          setFormData({ ...formData, warehouseId: newValue?.warehouseId || null });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t.warehouse}
                            error={!!errors.location}
                            helperText={errors.location}
                          />
                        )}
                      />
                    )}

                    {locationType === 'branch' && (
                      <Autocomplete
                        options={branches}
                        getOptionLabel={(option) => `${option.branchCode} - ${isRTL ? option.branchNameAr : option.branchName}`}
                        value={selectedBranch}
                        onChange={(_, newValue) => {
                          setSelectedBranch(newValue);
                          setFormData({ ...formData, branchId: newValue?.branchId || null });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t.branch}
                            error={!!errors.location}
                            helperText={errors.location}
                          />
                        )}
                      />
                    )}

                    {!locationType && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        {t.itemsLoaded}
                      </Alert>
                    )}
                  </>
                )}
              </>
            )}
          </Box>
        </Box>

        {stockCount && (
          <>
            <Box sx={{ mb: 2, p: 2, bgcolor: isDarkMode ? 'rgba(102, 126, 234, 0.1)' : '#f5f7ff', borderRadius: 2 }}>
              <Typography variant="body1" fontWeight="bold" color="primary">
                {stockCount.details.length} {t.locationSelected}
              </Typography>
            </Box>

            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#667eea' }}>
              {t.details}
            </Typography>

            <Box ref={gridRef}>
            <TableContainer component={Paper} elevation={2} sx={{ mb: 2, maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.item}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.unit}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">{t.systemQty}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.physicalQty}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">{t.variance}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">{t.varianceValue}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.notes}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockCount.details.map((detail) => {
                    const line = detailLines.find(l => l.countDetailId === detail.countDetailId);
                    const availableUnits = itemUnitsMap[detail.itemId] || [];
                    const selectedUnit = selectedDisplayUnits[detail.countDetailId];
                    const displaySysQty = getDisplaySystemQty(detail);
                    const displayUnitName = selectedUnit 
                      ? (isRTL ? selectedUnit.unitNameAr : selectedUnit.unitName)
                      : (isRTL ? detail.unitNameAr : detail.unitName);
                    return (
                      <TableRow key={detail.countDetailId} hover>
                        <TableCell>
                          <Typography fontWeight="500" fontSize="0.9rem">
                            {isRTL ? detail.itemNameAr : detail.itemName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {availableUnits.length > 1 ? (
                            <Autocomplete
                              size="small"
                              options={availableUnits}
                              getOptionLabel={(opt) => isRTL ? opt.unitNameAr : opt.unitName}
                              value={selectedUnit || undefined}
                              onChange={(_, newVal) => handleUnitChange(detail.countDetailId, newVal)}
                              disabled={stockCount.status === 'Approved'}
                              disableClearable
                              renderInput={(params) => <TextField {...params} variant="outlined" />}
                              sx={{ minWidth: 120 }}
                            />
                          ) : (
                            <Chip label={displayUnitName} size="small" color="info" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={displaySysQty.toFixed(3)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={physicalQuantityInputs[detail.countDetailId] ?? (line?.physicalQuantity?.toString() ?? '')}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                setPhysicalQuantityInputs(prev => ({ ...prev, [detail.countDetailId]: v }));
                                updateLine(detail.countDetailId, 'physicalQuantity', v === '' || v === '.' ? undefined : parseFloat(v));
                              }
                            }}
                            disabled={stockCount.status === 'Approved'}
                            inputProps={{ inputMode: 'decimal' }}
                            sx={{ maxWidth: 120 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {detail.variance !== null && detail.variance !== undefined && (
                            <Chip
                              label={detail.variance.toFixed(3)}
                              color={getVarianceColor(detail.variance)}
                              size="small"
                              icon={detail.variance >= 0 ? <CheckIcon /> : <ErrorIcon />}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {detail.varianceValue !== null && detail.varianceValue !== undefined && (
                            <Typography
                              fontWeight="bold"
                              color={detail.varianceValue >= 0 ? 'success.main' : 'error.main'}
                            >
                              {detail.varianceValue.toFixed(3)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={line?.notes ?? ''}
                            onChange={(e) => updateLine(detail.countDetailId, 'notes', e.target.value)}
                            disabled={stockCount.status === 'Approved'}
                            sx={{ maxWidth: 200 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            </Box>
          </>
        )}

        <TextField
          fullWidth
          label={t.notes}
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          multiline
          rows={3}
          disabled={stockCount?.status === 'Approved'}
        />
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5', justifyContent: 'space-between' }}>
        <Box>
          {isEditMode && canOverrideCost && stockCount && (
            <Button
              onClick={() => {
                if (stockCount.status === 'Posted') {
                  toast.error(isRTL ? 'ألغِ الترحيل أولاً ثم عدّل التكلفة' : 'Unpost first, then edit cost');
                  return;
                }
                if (stockCount.status === 'Approved') {
                  toast.error(isRTL ? 'ألغِ الاعتماد أولاً ثم عدّل التكلفة' : 'Unapprove first, then edit cost');
                  return;
                }
                setManualCostDialogOpen(true);
              }}
              size="large"
              variant="outlined"
              color="warning"
              startIcon={<BuildIcon />}
            >
              {isRTL ? '🔧 تعديل التكلفة يدوياً' : '🔧 Manual Cost'}
            </Button>
          )}
        </Box>
        <Box>
          <Button onClick={handleClose} disabled={loading} size="large">
            {t.cancel}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || stockCount?.status === 'Approved'}
            startIcon={loading && <CircularProgress size={20} />}
            size="large"
            sx={{
              ml: 1,
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
              '&:hover': { background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)' }
            }}
          >
            {t.save}
          </Button>
        </Box>
      </DialogActions>

      {isEditMode && stockCount && (
        <ManualCostEditDialog
          open={manualCostDialogOpen}
          onClose={() => setManualCostDialogOpen(false)}
          onSaved={() => { onSave(); }}
          docType="stockCount"
          docId={stockCount.stockCountId}
          docNumber={stockCount.countNumber || ''}
          docStatus={stockCount.status || 'InProgress'}
          lines={(stockCount.details || []).map((d: any): ManualCostLineRow => ({
            detailId: d.countDetailId,
            lineLabel: String(d.countDetailId),
            itemCode: d.itemCode || '',
            itemName: isRTL ? (d.itemNameAr || d.itemName) : (d.itemName || d.itemNameAr || ''),
            unitName: isRTL ? (d.unitNameAr || d.unitName) : (d.unitName || d.unitNameAr || ''),
            quantity: d.physicalQuantity ?? d.systemQuantity ?? 0,
            originalCost: d.isManualCost && d.originalCost != null ? d.originalCost : d.unitCost,
            currentUnitCost: d.unitCost,
            isManualCost: !!d.isManualCost,
            manualCostByName: d.manualCostByName,
            manualCostAt: d.manualCostAt,
          }))}
        />
      )}
    </Dialog>
  );
};

export default StockCountDialog;